/* Welcome pop-up: shows once per visitor, applies the discount code to the session in place. */
const SIM = window.SIM || {};
const DAY = 864e5;

if (!customElements.get('welcome-popup')) {
  customElements.define('welcome-popup', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.code = this.dataset.code;
      this.key = `sim:welcome:${this.code}`;
      this.applyLink = this.querySelector('[data-apply-code]');
      this.copyBtn = this.querySelector('[data-copy-code]');

      this.applyLink.addEventListener('click', (e) => this.apply(e));
      if (navigator.clipboard && this.copyBtn) {
        this.copyBtn.hidden = false;
        this.copyBtn.addEventListener('click', () => this.copy());
      }
      this.dialog.addEventListener('close', () => {
        if (this.read().state !== 'applied' && !this.hasAttribute('data-design-mode')) this.write('dismissed');
      });

      if (this.hasAttribute('data-design-mode')) {
        // Theme editor: open while the section is selected, never on its own.
        this.onSelect = (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.open(); };
        this.onDeselect = (e) => { if (e.detail.sectionId === this.dataset.sectionId) SIM.closeDialog(this.dialog); };
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
        return;
      }
      if (!this.shouldShow()) return;
      this.timer = setTimeout(() => this.openWhenFree(), Number(this.dataset.delay || 0) * 1000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
    }

    read() {
      try { return JSON.parse(localStorage.getItem(this.key)) || {}; } catch { return {}; }
    }

    write(state) {
      try { localStorage.setItem(this.key, JSON.stringify({ state, at: Date.now() })); } catch { /* private mode */ }
    }

    shouldShow() {
      const cookie = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
      if (cookie && decodeURIComponent(cookie[1]).toUpperCase() === this.code) return false;
      const { state, at } = this.read();
      if (state === 'applied') return false;
      if (state === 'dismissed' && Date.now() - at < Number(this.dataset.days || 14) * DAY) return false;
      return true;
    }

    // Never stack on top of the cart drawer, menu or quick add — wait until they close.
    openWhenFree() {
      if (document.querySelector('dialog[open]')) {
        this.timer = setTimeout(() => this.openWhenFree(), 2000);
        return;
      }
      this.open();
    }

    open() {
      if (SIM.openDialog) SIM.openDialog(this.dialog);
      else this.dialog.showModal();
    }

    async apply(e) {
      e.preventDefault();
      const link = this.applyLink;
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      this.querySelector('[data-error]').hidden = true;
      try {
        // Same route as the link; redirecting to cart.js keeps the response tiny.
        const res = await fetch(`/discount/${encodeURIComponent(this.code)}?redirect=/cart.js`, { credentials: 'same-origin' });
        const cart = await res.json();
        const saved = (cart.discount_codes || []).some((d) => d.code.toUpperCase() === this.code);
        if (!res.ok || !saved) throw new Error('not applied');
        this.write('applied');
        this.querySelector('[data-offer]').hidden = true;
        this.querySelector('[data-applied]').hidden = false;
        this.querySelector('[data-applied] a').focus();
        this.refreshCart();
      } catch {
        this.querySelector('[data-error]').hidden = false;
      } finally {
        link.removeAttribute('aria-busy');
      }
    }

    // Re-render the bag so the discount line shows straight away if it already has items.
    async refreshCart() {
      const ids = SIM.cart && SIM.cart.sections ? SIM.cart.sections() : [];
      if (!ids.length) return;
      try {
        const res = await fetch(`${SIM.config.routes.root_url}?sections=${ids.join(',')}`);
        SIM.cart.render(await res.json());
        SIM.emit('cart:updated', { source: 'discount' });
      } catch { /* the bag updates on next change anyway */ }
    }

    async copy() {
      try {
        await navigator.clipboard.writeText(this.code);
        if (SIM.toast) SIM.toast(this.copyBtn.dataset.copied || this.copyBtn.textContent);
      } catch { /* clipboard blocked: code is visible to copy by hand */ }
    }
  });
}
